import { signalStore, withMethods, withState } from '@ngrx/signals';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import {
  removeAllEntities,
  removeEntity,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { GetMessagesRequest, Message, MessagePaginatedResponse } from '@clientwire/api';
import { inject } from '@angular/core';
import { MessagesWithConversationId, MessageUpdateEvent } from '../interfaces';
import { logger, SharedUtils } from '../utils';
import { WindowService, WireApiService } from '../services';
import { ErrorStore } from './error.store';

export const MessageStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withDevtools('conversation-message-store'),
  withState({ error: null }),
  withEntities<MessagesWithConversationId>(),
  withMethods((store) => {
    const wireApiService = inject(WireApiService);
    const errorStore = inject(ErrorStore);
    const windowService = inject(WindowService);

    return {
      async addMessages(
        conversationId: string,
        newMessages: Message[],
        lastUpdateEvent: MessageUpdateEvent = 'localMessage',
        position: 'prepend' | 'append' = 'prepend',
        overwrite = true,
        secureToAdd = false
      ) {
        let oldMessages = [...(store.entityMap()[conversationId]?.messages ?? [])];

        const filteredMessages = this.filterOutExistingMessages(
          conversationId,
          oldMessages,
          newMessages,
          overwrite,
          lastUpdateEvent
        );

        // Validate filtered messages
        if (!this.hasValidMessages(filteredMessages)) {
          return;
        }

        // Check for missing messages and fetch if needed
        if (!secureToAdd && oldMessages.length > 0) {
          await this.checkAndFetchMissingMessages(
            conversationId,
            filteredMessages,
            oldMessages,
            position
          );
          oldMessages = [...(store.entityMap()[conversationId]?.messages ?? [])];
        }

        // Merge and sort messages
        this.mergeAndUpdateMessages(
          conversationId,
          oldMessages,
          filteredMessages,
          position,
          lastUpdateEvent
        );
      },

      hasValidMessages(filteredMessages: Message[]): boolean {
        if (filteredMessages.length === 0 || !filteredMessages[0]) {
          return false;
        }
        if (!filteredMessages[filteredMessages.length - 1]) {
          return false;
        }
        return true;
      },

      async checkAndFetchMissingMessages(
        conversationId: string,
        filteredMessages: Message[],
        oldMessages: Message[],
        position: 'prepend' | 'append'
      ): Promise<void> {
        const sequences = this.extractSequences(filteredMessages, oldMessages, position);

        if (!this.allSequencesValid(sequences)) {
          return;
        }

        // After validation, we know these are numbers
        const sequenceDiff = (sequences.newCompare as number) - (sequences.oldCompare as number);
        await this.handleSequenceDifference(conversationId, sequenceDiff, sequences, position);
      },

      extractSequences(
        filteredMessages: Message[],
        oldMessages: Message[],
        position: 'prepend' | 'append'
      ) {
        const newLatest = filteredMessages[0]?.sequence;
        const newOldest = filteredMessages[filteredMessages.length - 1]?.sequence;
        const oldLatest = oldMessages[0]?.sequence;
        const oldOldest = oldMessages[oldMessages.length - 1]?.sequence;

        const newCompare = position === 'append' ? newLatest : newOldest;
        const oldCompare = position === 'append' ? oldOldest : oldLatest;

        return { newLatest, newOldest, oldLatest, oldOldest, newCompare, oldCompare };
      },

      allSequencesValid(sequences: {
        newLatest: number | null | undefined;
        newOldest: number | null | undefined;
        oldLatest: number | null | undefined;
        oldOldest: number | null | undefined;
        newCompare: number | null | undefined;
        oldCompare: number | null | undefined;
      }): boolean {
        return [
          sequences.newLatest,
          sequences.newOldest,
          sequences.oldLatest,
          sequences.oldOldest,
          sequences.newCompare,
          sequences.oldCompare,
        ].every((seq) => seq !== null && seq !== undefined);
      },

      async handleSequenceDifference(
        conversationId: string,
        sequenceDiff: number,
        sequences: {
          newOldest: number | null | undefined;
          oldLatest: number | null | undefined;
          oldOldest: number | null | undefined;
          newLatest: number | null | undefined;
        },
        position: 'prepend' | 'append'
      ): Promise<void> {
        if (sequenceDiff > 1) {
          await this.fetchMissingMessages(
            conversationId,
            sequences.newOldest as number,
            sequences.oldLatest as number,
            position
          );
        } else if (sequenceDiff < -1) {
          await this.fetchMissingMessages(
            conversationId,
            sequences.oldLatest as number,
            sequences.newOldest as number,
            position
          );
        } else if (sequenceDiff === 0) {
          await this.handleZeroSequenceDiff(conversationId, sequences, position);
        }
      },

      async handleZeroSequenceDiff(
        conversationId: string,
        sequences: {
          oldOldest: number | null | undefined;
          oldLatest: number | null | undefined;
          newOldest: number | null | undefined;
          newLatest: number | null | undefined;
        },
        position: 'prepend' | 'append'
      ): Promise<void> {
        const oldestMessageSequence = Math.min(
          sequences.oldOldest as number,
          sequences.oldLatest as number
        );
        const latestMessageSequence = Math.max(
          sequences.newOldest as number,
          sequences.newLatest as number
        );

        await this.fetchMissingMessages(
          conversationId,
          latestMessageSequence,
          oldestMessageSequence,
          position
        );
      },

      mergeAndUpdateMessages(
        conversationId: string,
        oldMessages: Message[],
        filteredMessages: Message[],
        position: 'prepend' | 'append',
        lastUpdateEvent: MessageUpdateEvent
      ): void {
        const messages = (
          position === 'append'
            ? [...oldMessages, ...filteredMessages]
            : [...filteredMessages, ...oldMessages]
        ).sort(SharedUtils.sortBy('sequence'));

        updateState(
          store,
          'addMessages',
          updateEntity({
            id: conversationId,
            changes: {
              messages,
              lastUpdateEvent,
              messageAppendLoading: false,
              messagePrependLoading: false,
            },
          })
        );
      },

      replaceMessage(
        conversationId: string,
        newMessage: Message,
        lastUpdateEvent: MessageUpdateEvent
      ) {
        const oldMessages = [...(store.entityMap()[conversationId]?.messages ?? [])];
        const foundIndex = oldMessages.findIndex(
          (message) => message.sourceId === newMessage.sourceId
        );
        if (foundIndex > -1) {
          const oldMessage = oldMessages[foundIndex];
          let messageToReplace = newMessage;

          // Specific scenario: preserve data: downloadUrl when transitioning from local to server message
          if (
            oldMessage &&
            oldMessage.id.startsWith('new-') &&
            lastUpdateEvent === 'socketMessage' &&
            !newMessage.asset?.downloadUrl &&
            oldMessage.asset?.downloadUrl?.startsWith('data:')
          ) {
            messageToReplace = {
              ...newMessage,
              asset: {
                ...newMessage.asset,
                downloadUrl: oldMessage.asset.downloadUrl,
              },
            };
          }

          oldMessages.splice(foundIndex, 1, messageToReplace);
          const sortedMessages = [...oldMessages].sort(SharedUtils.sortBy('sequence'));
          updateState(
            store,
            'replaceMessage',
            updateEntity({
              id: conversationId,
              changes: {
                messages: sortedMessages,
                lastUpdateEvent,
                messageAppendLoading: false,
                messagePrependLoading: false,
              },
            })
          );
        }
      },

      removeMessage(
        conversationId: string,
        messageId?: string,
        lastUpdateEvent: MessageUpdateEvent = 'localMessage'
      ) {
        const oldMessages = [...(store.entityMap()[conversationId]?.messages ?? [])];
        if (!messageId) {
          if (oldMessages.length > 0) {
            oldMessages.shift();
            updateState(
              store,
              'removeMessage',
              updateEntity({
                id: conversationId,
                changes: { messages: oldMessages, lastUpdateEvent },
              })
            );
          } else {
            logger.error('No messages to remove');
          }
        } else {
          const foundIndex = oldMessages.findIndex((message) => message.id === messageId);
          if (foundIndex > -1) {
            oldMessages.splice(foundIndex, 1);
            updateState(
              store,
              'removeMessage',
              updateEntity({
                id: conversationId,
                changes: { messages: oldMessages, lastUpdateEvent },
              })
            );
          } else {
            logger.error('Message not found');
          }
        }
      },

      removeMessages(conversationId: string) {
        updateState(store, 'removeMessages', removeEntity(conversationId));
      },

      clearMessages() {
        updateState(store, 'Clear MessageStore', { error: null });
        updateState(store, 'Clear entities', removeAllEntities());
      },

      async getMessagesOfConversation(
        conversationId: string,
        limit: number,
        lastSequence?: number,
        position: 'prepend' | 'append' = 'prepend',
        secureToAdd = false,
        order: 'ASC' | 'DESC' = 'DESC',
        fetchUntilHasMoreMessages = false
      ): Promise<MessagePaginatedResponse | undefined> {
        errorStore.clearErrorsByLocations([`${conversationId}:getMessagesOfConversation`]);

        const headers = this.buildMessageHeaders(conversationId, limit, lastSequence, order);

        try {
          const response = await wireApiService.messagesApi.getMessages(headers);
          await this.handleMessagesResponse(
            response,
            conversationId,
            position,
            secureToAdd,
            order,
            limit,
            fetchUntilHasMoreMessages
          );
          return response;
        } catch (error) {
          return await this.handleMessagesError(error, conversationId, position);
        }
      },

      buildMessageHeaders(
        conversationId: string,
        limit: number,
        lastSequence?: number,
        order?: 'ASC' | 'DESC'
      ): GetMessagesRequest {
        const headers: GetMessagesRequest = {
          conversationId,
          limit,
        };

        if (lastSequence) {
          headers.lastSequence = lastSequence;
          headers.order = order;
        }

        return headers;
      },

      async handleMessagesResponse(
        response: MessagePaginatedResponse,
        conversationId: string,
        position: 'prepend' | 'append',
        secureToAdd: boolean,
        order: 'ASC' | 'DESC',
        limit: number,
        fetchUntilHasMoreMessages: boolean
      ): Promise<void> {
        if (response.messages?.length > 0) {
          this.processAndAddMessages(
            response.messages,
            conversationId,
            position,
            secureToAdd,
            order
          );
          await this.fetchMoreMessagesIfNeeded(
            response,
            conversationId,
            limit,
            position,
            secureToAdd,
            order,
            fetchUntilHasMoreMessages
          );
        } else {
          this.setMessageLoading(conversationId, position, false);
        }
      },

      processAndAddMessages(
        messages: Message[],
        conversationId: string,
        position: 'prepend' | 'append',
        secureToAdd: boolean,
        order: 'ASC' | 'DESC'
      ): void {
        if (order === 'ASC') {
          const reversedMessages = [...messages].reverse();
          this.addMessages(
            conversationId,
            reversedMessages,
            'serverMessages',
            position,
            true,
            secureToAdd
          );
        } else if (order === 'DESC') {
          this.addMessages(conversationId, messages, 'serverMessages', position, true, secureToAdd);
        } else {
          throw new Error('Invalid order');
        }
      },

      async fetchMoreMessagesIfNeeded(
        response: MessagePaginatedResponse,
        conversationId: string,
        limit: number,
        position: 'prepend' | 'append',
        secureToAdd: boolean,
        order: 'ASC' | 'DESC',
        fetchUntilHasMoreMessages: boolean
      ): Promise<void> {
        if (!fetchUntilHasMoreMessages || !response.hasMore) {
          return;
        }

        const lastMessageSequence = response.messages[response.messages.length - 1]?.sequence;
        if (lastMessageSequence) {
          this.getMessagesOfConversation(
            conversationId,
            limit,
            lastMessageSequence,
            position,
            secureToAdd,
            order,
            true
          );
        }
      },

      async handleMessagesError(
        error: unknown,
        conversationId: string,
        position: 'prepend' | 'append'
      ): Promise<undefined> {
        this.setMessageLoading(conversationId, position, false);
        logger.error(error);

        try {
          await errorStore.addErrorResponse(
            error,
            'inline',
            true,
            `${conversationId}:getMessagesOfConversation`
          );
          return undefined;
        } catch {
          await errorStore.addErrorResponse(
            { code: 'error.messages.getMessagesOfConversation' },
            'inline',
            false,
            `${conversationId}:getMessagesOfConversation`
          );
          throw error;
        }
      },

      setMessageLoading(conversationId: string, position: 'prepend' | 'append', loading: boolean) {
        if (position === 'append') {
          updateState(
            store,
            'messageAppendLoading',
            updateEntity({
              id: conversationId,
              changes: { messageAppendLoading: loading, lastUpdateEvent: 'loadingChange' },
            })
          );
        } else {
          updateState(
            store,
            'messagePrependLoading',
            updateEntity({
              id: conversationId,
              changes: { messagePrependLoading: loading, lastUpdateEvent: 'loadingChange' },
            })
          );
        }
      },

      recalculateMessagesLimit(conversationId: string, element: HTMLElement) {
        const limit = windowService.calculateLimitSize(element);
        updateState(
          store,
          'recalculateMessagesLimit',
          updateEntity({ id: conversationId, changes: { messagesLimit: limit } })
        );
        return limit;
      },

      async sleep(time: number) {
        return new Promise((resolve) => setTimeout(resolve, time));
      },

      async sendLocalMessageToServer(localMessage: Message) {
        try {
          const conversationId = localMessage.conversationId;
          const serverMessage = await wireApiService.messagesApi.createMessage({
            conversationId: localMessage.conversationId,
            messagePostRequest: {
              kind: localMessage.kind,
              text: localMessage.text,
              location: localMessage.location,
              formattedAddress: localMessage.formattedAddress,
              participantId: localMessage.participantId,
              sourceId: localMessage.sourceId,
            },
          });

          if (serverMessage.asset) {
            serverMessage.asset.downloadUrl = localMessage.asset?.downloadUrl;
          }

          this.replaceMessage(conversationId, serverMessage, 'serverMessages');

          return serverMessage;
        } catch (err) {
          logger.error(err);
          this.addMessageIdToErrors(localMessage);
          return null;
        }
      },

      addMessageIdToErrors(localMessage: Message) {
        const entry = store.entityMap()[localMessage.conversationId];
        if (entry) {
          const errorIds = entry.messageIdsWithError ?? [];
          updateState(
            store,
            'setDownloadUrl',
            updateEntity({
              id: localMessage.conversationId,
              changes: { messageIdsWithError: [...errorIds, localMessage.id] },
            })
          );
        }
      },

      removeMessageIdFromErrors(conversationId: string, messageId: string) {
        const messageIdsWithError = [
          ...(store.entityMap()[conversationId]?.messageIdsWithError ?? []),
        ];
        messageIdsWithError.splice(messageIdsWithError.indexOf(messageId), 1);
        updateState(
          store,
          'remove messageId from messageIdsWithError',
          updateEntity({ id: conversationId, changes: { messageIdsWithError } })
        );
      },

      setDownloadUrl(downloadUrl: string, messageId: string, conversationId: string) {
        const messages = [...(store.entityMap()[conversationId]?.messages ?? [])];
        const foundIndex = messages.findIndex((message) => message.id === messageId);
        if (foundIndex > -1 && messages[foundIndex]?.asset) {
          messages[foundIndex].asset.downloadUrl = downloadUrl;
          updateState(
            store,
            'setDownloadUrl',
            updateEntity({ id: conversationId, changes: { messages } })
          );
        }
      },

      filterOutExistingMessages(
        conversationId: string,
        oldMessages: Message[],
        newMessages: Message[],
        overwrite: boolean,
        lastUpdateEvent: MessageUpdateEvent = 'localMessage'
      ) {
        return newMessages.filter((newMessage) => {
          const indexOfExistingMessage = oldMessages.findIndex(
            (message) => message.id === newMessage.id || message.sourceId === newMessage.sourceId
          );
          if (indexOfExistingMessage > -1) {
            if (overwrite) {
              this.replaceMessage(conversationId, newMessage, lastUpdateEvent);
            }
            return false;
          }
          return true;
        });
      },

      async fetchMissingMessages(
        conversationId: string,
        toSequence: number,
        fromSequence: number,
        position: 'prepend' | 'append'
      ) {
        const totalMissing = Math.abs(toSequence - fromSequence) - 1;

        if (totalMissing <= 0) {
          return; // No messages to fetch
        }

        let remainingToFetch = totalMissing;
        let currentFromSequence = fromSequence;

        logger.warn(
          `Fetching ${totalMissing} missing messages between ${fromSequence} and ${toSequence}`
        );

        while (remainingToFetch > 0) {
          const limit = Math.min(remainingToFetch, 100);

          logger.debug(`Fetching batch: ${limit} messages, remaining: ${remainingToFetch}`);

          await this.getMessagesOfConversation(
            conversationId,
            limit,
            currentFromSequence,
            position,
            true,
            position === 'append' ? 'DESC' : 'ASC'
          );

          // Update counters for next iteration
          remainingToFetch -= limit;

          // Update currentFromSequence based on the order/direction
          if (position === 'append') {
            // For append (DESC order), we're going backwards in sequence numbers
            currentFromSequence = currentFromSequence - limit;
          } else {
            // For prepend (ASC order), we're going forwards in sequence numbers
            currentFromSequence = currentFromSequence + limit;
          }
        }

        logger.debug('Finished fetching all missing messages');
      },
    };
  })
);
